import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { t } from "../i18n/t";

/**
 * §B7.4: an error toast is `role="alert"` and carries the request id (§A3.3), which is the only thing that lets
 * an operator and a log line be joined up afterwards.
 */
export type ToastTone = "info" | "error";

export type ToastMessage = { id: string; message: string; requestId: string | null; tone: ToastTone };

type ToastApi = {
  toasts: ToastMessage[];
  push: (message: string, requestId?: string | null, tone?: ToastTone) => void;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Fable F16: an error sits longer than a routine confirmation, but both auto-dismiss - a toast nobody
 * clicks "Dismiss" on used to sit on screen forever.
 */
const AUTO_DISMISS_MS: Record<ToastTone, number> = { info: 5000, error: 8000 };

/**
 * Fable F4: React StrictMode double-invokes a mount effect, so `ConnectionsScreen`'s "turn the query
 * string into a toast once, on mount" `useEffect` can call `push` with the identical message twice before
 * `setParams({})` has taken effect and stripped the query string that triggered it. A second `push` for
 * the same text within this window is the double render talking to itself, not two different events, so
 * it is dropped rather than shown as a second stacked banner.
 */
const DEDUPE_WINDOW_MS = 2000;

export function ToastProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  // Message text + the time it was last (re)shown, kept outside React state so a dedupe check never waits
  // on a render to see the previous push - two pushes in the same microtask must both see it.
  const recentRef = useRef(new Map<string, number>());
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const clearTimer = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    },
    [clearTimer],
  );

  const push = useCallback(
    (message: string, requestId?: string | null, tone: ToastTone = "info") => {
      const now = Date.now();
      const last = recentRef.current.get(message);
      if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return;
      recentRef.current.set(message, now);

      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { id, message, requestId: requestId ?? null, tone }]);
      timersRef.current.set(
        id,
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS[tone]),
      );
    },
    [dismiss],
  );

  // Every pending auto-dismiss timer is cleared on unmount so it never fires `setState` on a torn-down
  // provider (a strict-mode remount, or the app itself unmounting mid-timeout).
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastApi>(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts">
        {toasts.map((toast) => (
          <div
            className={toast.tone === "error" ? "toast toast--error" : "toast"}
            role="alert"
            key={toast.id}
          >
            <p>{toast.message}</p>
            {toast.requestId === null ? null : (
              <p className="mono">{t("toast.request_id", { id: toast.requestId })}</p>
            )}
            <button type="button" className="button button--compact" onClick={() => dismiss(toast.id)}>
              {t("toast.dismiss")}
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const value = useContext(ToastContext);
  if (value === null) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return value;
}
